// Resolving a CDN image URL for display, now that CDN URLs are SIGNED.
//
// Spec: docs/specs/2026-10-03-signed-image-url-design.md §6.4 (this repo is the
// one listed as "the one that actually breaks if nothing is done").
//
// --------------------------------------------------------------------------
// WHAT CHANGED
//
// The App API appends `?e=<unix>&s=<sig>&k=<keyid>` to every cdn.madamefin.com
// image URL it returns, and the CDN Worker answers 403 to any request whose
// query is missing, truncated or edited. Two consequences for this repo:
//
// 1. A URL the BACKEND resolved must be rendered byte for byte. Nothing here
//    may re-encode it, cut it at "?", or append a cache-buster — each of those
//    turns a working image into a 403.
// 2. A URL this repo COMPOSES ITSELF from a bare object key can never be
//    signed (the secret lives in the API and the Worker, not in a browser
//    bundle), so client-side minting is dead code the moment the Worker
//    enforces. The payloads already carry a resolved URL —
//    `Catalog_product.go:486,540` and `Catalog_promotion_bundle.go:1136` call
//    resolveImage at read time — so the honest answer for a bare key is "no
//    image", which the callers already render as an empty cell.
//
// The one host rewrite that stays is the legacy private-bucket one: rows
// written before the CDN cut-over hold `https://mdf-front.s3.<region>
// .amazonaws.com/<key>`, the bucket is private (direct GET = 403), and only
// cdn.madamefin.com can serve those bytes. Swapping just the origin keeps the
// path AND any query intact. Such a row has no signature, so it renders while
// the Worker is in `dual` and stops when it goes to `enforce` — the fix for
// those is the backfill in spec §3.5, not more string surgery here.

/** The only host that serves our image objects. */
const CDN_ORIGIN = 'https://cdn.madamefin.com';

/** Legacy direct-S3 origin of the (now private) product-image bucket. */
const LEGACY_S3_ORIGIN = /^https?:\/\/mdf-front\.s3[^/]*\.amazonaws\.com/i;

/**
 * cdnImageUrl(value) → a URL safe to put in `:src`, or "" when there is none.
 *
 * - absolute http(s): returned unchanged, EXCEPT the legacy private-S3 origin,
 *   which is swapped for the CDN origin. Path, query (the signature) and
 *   fragment are never touched.
 * - `data:` URI: unchanged (a just-picked local file preview).
 * - anything else (bare object key, "/key", blank): "" — see note 2 above.
 */
export function cdnImageUrl(value) {
  const raw = String(value === null || value === undefined ? '' : value).trim();
  if (raw === '') return '';
  if (/^data:/i.test(raw)) return raw;
  if (LEGACY_S3_ORIGIN.test(raw)) return raw.replace(LEGACY_S3_ORIGIN, CDN_ORIGIN);
  if (/^https?:\/\//i.test(raw)) return raw;
  // A bare key. Composing `CDN_ORIGIN + key` here would mint an UNSIGNED URL.
  return '';
}

/**
 * pickImageUrl(...candidates) → the first candidate that is renderable.
 *
 * Prefers a candidate that is already an absolute URL, because that is the one
 * the backend resolved AND SIGNED; a bare key sitting in an earlier field must
 * not shadow it. Falls back to the first non-blank candidate so a payload with
 * only keys behaves exactly as it did before (the caller still passes the
 * result through cdnImageUrl before rendering).
 */
export function pickImageUrl() {
  const vals = [];
  for (let i = 0; i < arguments.length; i++) {
    const v = arguments[i];
    const s = String(v === null || v === undefined ? '' : v).trim();
    if (s !== '') vals.push(s);
  }
  for (let i = 0; i < vals.length; i++) {
    if (/^(https?:\/\/|data:)/i.test(vals[i])) return vals[i];
  }
  return vals.length > 0 ? vals[0] : '';
}

/**
 * cdnObjectKey(url) → the S3 object key behind a CDN URL, signature removed.
 *
 * For code that has to send a KEY back to the API (the API stores keys, never
 * signed URLs — spec §3.3). The query must come off BEFORE the key is taken or
 * the key would carry `?e=…&s=…&k=…` and match no object in the bucket.
 * A value that is already a bare key is returned as-is (minus a leading "/").
 */
export function cdnObjectKey(url) {
  let raw = String(url === null || url === undefined ? '' : url).trim();
  if (raw === '') return '';
  const hash = raw.indexOf('#');
  if (hash >= 0) raw = raw.slice(0, hash);
  const q = raw.indexOf('?');
  if (q >= 0) raw = raw.slice(0, q);
  const m = raw.match(/^https?:\/\/[^/]+\/(.*)$/i);
  if (m) return m[1];
  return raw.replace(/^\/+/, '');
}

export default { cdnImageUrl, pickImageUrl, cdnObjectKey };
